import {
  ActionIcon,
  Affix,
  Box,
  Button,
  Container,
  Group,
  Paper,
  Text,
  Tooltip,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconCheck, IconCircleDot, IconMessageCircle, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export interface FormEditActionBarProps {
  isDirty: boolean;
  dirtyCount: number;
  isSaving: boolean;
  assistantOpen: boolean;
  onSave: () => void | Promise<void>;
  onToggleAssistant: () => void;
  onDelete: () => void;
}

export function FormEditActionBar({
  isDirty,
  dirtyCount,
  isSaving,
  assistantOpen,
  onSave,
  onToggleAssistant,
  onDelete,
}: FormEditActionBarProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 640px)');

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t('formEdit.deleteConfirmTitle'),
      children: <Text size="sm">{t('formEdit.deleteConfirmBody')}</Text>,
      labels: {
        confirm: t('formEdit.deleteConfirm'),
        cancel: t('formEdit.deleteCancel'),
      },
      confirmProps: { color: 'red' },
      onConfirm: onDelete,
    });
  };

  const status = isDirty ? (
    <Group gap={6} wrap="nowrap">
      <IconCircleDot size={16} color="var(--mantine-color-yellow-6)" />
      {!isMobile && (
        <Text size="sm" c="yellow.8">
          {t('formEdit.unsavedChanges', { count: dirtyCount })}
        </Text>
      )}
    </Group>
  ) : (
    <Group gap={6} wrap="nowrap">
      <IconCheck size={16} color="var(--mantine-color-teal-6)" />
      {!isMobile && (
        <Text size="sm" c="dimmed">
          {t('formEdit.allChangesSaved')}
        </Text>
      )}
    </Group>
  );

  return (
    <Affix position={{ bottom: 0, left: 0, right: 0 }} zIndex={200}>
      <Paper
        withBorder
        shadow="sm"
        radius={0}
        style={{
          borderLeft: 0,
          borderRight: 0,
          borderBottom: 0,
          backgroundColor: 'var(--mantine-color-body)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Container fluid py="xs" px="md">
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Box>{status}</Box>
            <Group gap="xs" wrap="nowrap">
              <Tooltip label={t('formEdit.delete')}>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  onClick={handleDelete}
                  aria-label={t('formEdit.delete')}
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
              <Button
                variant="subtle"
                size="sm"
                leftSection={<IconMessageCircle size={16} />}
                onClick={onToggleAssistant}
              >
                {assistantOpen ? t('formEdit.closeAssistant') : t('formEdit.openAssistant')}
              </Button>
              <Button size="sm" disabled={!isDirty} loading={isSaving} onClick={onSave}>
                {t('formEdit.save')}
              </Button>
            </Group>
          </Group>
        </Container>
      </Paper>
    </Affix>
  );
}
